// .../shared/production/use-refiner-production.ts

import produce from 'immer';
import { isEmpty } from 'psims/lib/empty';
import { recordActionAsEnum } from 'psims/models/api/data-submission-record-action';
import { UpdateMsoProductionDraft, UpdateMsoProductionField } from 'psims/models/submission-types/mso/annual-activity/refiner/update-mso-production';
import { UseFocusedField } from 'psims/react/util/use-focused-field';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cleanSubmissionEntityList } from '../../../shared/update-data-utils';
import useTrackChanges from '../../../shared/use-track-changes';
import { FieldValidation, FocusField, MsoProductView } from '../types';
import { UseMsoAnnualActivityRefData } from '../use-mso-annual-activity-ref-data';
import { getInlineMessage } from './refiner-production-inline-messages';
import { getNotificationMessage } from './refiner-production-notifications';
import { validateProduction, ValidationResult } from './refiner-production-validation';

interface ProgressController {
  currentStep: { kind: 'data' | 'submit' };
  goToStep: (index: number) => any;
}

// Shape coming in from the API response (annualActivities array entries)
export interface MsoProduction {
  id?: number;
  dataSubmissionId?: number;
  msoProductId?: number;
  totalAnnualVolume?: number | null;
  totalProductionAnnualVolume?: number | null;  
  indicativeMSOBasedVolume?: number | null;      
  concurrencyToken?: string;
  recordResult?: { rowResult?: string | null } | null;
}

interface UseRefinerProductionProps {
  dataSubmissionId: number;
  focusFieldCtrl: UseFocusedField<FocusField>;
  initialData: Array<MsoProduction>;
  progressCtrl: ProgressController;
  refData: UseMsoAnnualActivityRefData;
}

function useRefinerProduction({
  dataSubmissionId,
  focusFieldCtrl,
  initialData,
  progressCtrl,
  refData,
}: UseRefinerProductionProps) {
  // Build a targetDays lookup: msoProductId -> noOfDays
  const targetDaysMap = useMemo(() => {
    return (refData.msoTargetDays ?? []).reduce<Record<number, number>>(
      (acc, t) => { acc[t.msoProductId] = t.noOfDays; return acc; },
      {}
    );
  }, [refData.msoTargetDays]);
const initialDataAsUpdate = useMemo(() => {
  return refData.products
    .filter(p => p.productStatus === 'active')
    .map(({ id }) => {
      const existing = initialData.find(
        d =>
          d.msoProductId === id &&
          d.recordResult?.rowResult !== 'Deleted' &&
          d.totalProductionAnnualVolume != null
      );

      if (existing) {
        return toUpdateMsoProductionDraft(existing, targetDaysMap, dataSubmissionId);  // add
      }

      return toUpdateMsoProductionDraft(
        {
          dataSubmissionId,
          msoProductId: id,
          totalProductionAnnualVolume: null,
          indicativeMSOBasedVolume: null,
        },
        targetDaysMap,
        dataSubmissionId  // add
      );
    });
}, [initialData, targetDaysMap, refData.products, dataSubmissionId]);

  const [data, setData] = useState(initialDataAsUpdate);

  const changeCtrl = useTrackChanges({
    currentData: data,
    dataFields: ['totalProductionAnnualVolume'],
    initialData: initialDataAsUpdate,
  });

  

  const updateProduction = useCallback(
  (prod: UpdateMsoProductionDraft) => {
    setData(prev =>
      produce(prev, draft => {
        const idx = prev.findIndex(s => s._id === prod._id);
        const targetDays = targetDaysMap[prod.msoProductId];

        const indicativeMSOBasedVolume =
          prod.totalProductionAnnualVolume != null && targetDays != null
            ? Math.round(Number(prod.totalProductionAnnualVolume) * (targetDays / 365))
            : null;

        draft[idx] = {
          ...prod,
          indicativeMSOBasedVolume,
          recordAction: recordActionAsEnum(prod.id == null ? 'Create' : 'Update'),
        };
      })
    );
  },
  [targetDaysMap]  // add targetDaysMap to deps
);

const dataView = useMemo(() => {
  return data.map(prod => {
    const validationResults = validateProduction(prod, refData);
    const targetDays = targetDaysMap[prod.msoProductId];

    return {
      data: prod,
      indicativeMSO: prod.indicativeMSOBasedVolume,
      targetDays,
      validations: validationResults.reduce(
        (map, vr) => ({
          ...map,
          [vr.key]: {
            inline: getInlineMessage(vr),
            notification: {
              parts: getNotificationMessage(
                () => focusFieldCtrl.setFocusedField({
                  _id: prod._id,
                  field: vr.key,
                  kind: 'data',
                }),
                refData.productIdMap[prod.msoProductId]?.name ?? '',
                vr
              ),
            },
            validationResult: vr,
          },
        }),
        {} as { [key in UpdateMsoProductionField]?: FieldValidation<ValidationResult> }
      ),
    };
  });
}, [data, focusFieldCtrl, refData, targetDaysMap]);

  const requestEntities = useMemo(() => cleanSubmissionEntityList(data), [data]);

  const anyInvalid = useMemo(
    () => dataView.some(v => !isEmpty(v.validations)),
    [dataView]
  );

  // Reset on step change away from data
 const currentStepKind = progressCtrl.currentStep.kind;
const prevInitialDataLengthRef = useRef(initialData.length);
const prevFirstIdRef = useRef(initialData[0]?.id);

useEffect(() => {
  const lengthChanged = prevInitialDataLengthRef.current !== initialData.length;
  const firstIdChanged = prevFirstIdRef.current !== initialData[0]?.id;
  
  if (lengthChanged || firstIdChanged) {
    prevInitialDataLengthRef.current = initialData.length;
    prevFirstIdRef.current = initialData[0]?.id;
    setData(initialDataAsUpdate);
  }
}, [initialData, initialDataAsUpdate]);
// Reset when initialData changes after a successful save
///useEffect(() => {
 // setData(initialDataAsUpdate);
//}, [initialDataAsUpdate]);

  return {
    anyInvalid,
    hasChanges: changeCtrl.hasChanges,
    requestEntities,
    productionView: dataView,
    updateProduction,
  };
}

function toUpdateMsoProductionDraft(
  prod: MsoProduction,
  targetDaysMap: Record<number, number>,
  dataSubmissionId: number  // add
): UpdateMsoProductionDraft {
  const { recordResult, ...data } = prod;

  const targetDays = prod.msoProductId != null ? targetDaysMap[prod.msoProductId] : undefined;
  const indicativeMSOBasedVolume =
    prod.indicativeMSOBasedVolume != null
      ? prod.indicativeMSOBasedVolume
      : prod.totalProductionAnnualVolume != null && targetDays != null
        ? Math.round(Number(prod.totalProductionAnnualVolume) * (targetDays / 365))
        : null;

  return {
  ...(data as UpdateMsoProductionDraft),
  dataSubmissionId: data.dataSubmissionId ?? dataSubmissionId,
   msoProductId: data.msoProductId!,  // will need to pass id through
  indicativeMSOBasedVolume,
 recordAction: data.id != null ? recordActionAsEnum('Update') : undefined,
  _id: Symbol(),
};
}

export default useRefinerProduction;
export type UseRefinerProduction = ReturnType<typeof useRefinerProduction>;